import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import TextInput from 'src/components/TextInput';
import { isButtonEnabled } from 'src/components/design-system/Button';
import useDelayedInputOnchange from 'src/hooks/useDelayedInputOnchange';
import { round } from 'src/utils';
import { getBgColor } from 'src/utils/icons';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';
import OcrIndicator from '../split_line_item/OcrIndicator';

const ItemTextField: FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const getMatchingOcrLineItem = useContextSelector(
    InvoiceContext,
    (value) => value.getMatchingOcrLineItem
  );
  const handleHoverOcrField = useContextSelector(
    InvoiceContext,
    (value) => value.handleHoverOcrField
  );
  const initialInvoice = useContextSelector(InvoiceContext, (value) => value.initialInvoice);

  const { handleChange, value } = useDelayedInputOnchange(props, true);
  const error = useMemo(() => dataItem?.errors?.productItemNum, [dataItem?.errors?.productItemNum]);

  const matchingItem = getMatchingOcrLineItem(dataItem.receiptLineItemId);
  const ocrConfidence = matchingItem.reduce(
    (acc, item) => (acc += parseFloat(item.item.itemNumber.confidence || '0')),
    0
  );
  const averageOcrConfidence = round(ocrConfidence / matchingItem.length, 2);
  const isEdited = useMemo(
    () =>
      matchingItem[0]?.item?.itemNumber?.value?.toLowerCase()?.trim() !==
      value.toLowerCase()?.trim(),
    [matchingItem, value]
  );
  const matchItemIndex = matchingItem.map(
    (item) => Number(item.item?.itemSequenceNumber?.value) - 1
  );
  const handleHover = useCallback(() => {
    handleHoverOcrField('itemNumber', matchItemIndex);
  }, [handleHoverOcrField, matchItemIndex]);
  const tooltip = value.replace('\n', '');

  return (
    <GridCellBase {...props} className={getBgColor(error)} disableToolTip>
      <div className="flex flex-row gap-1 items-center justify-between">
        <div className="truncate" data-tooltip-id={'global-tooltip'} data-tooltip-content={tooltip}>
          {Number(dataItem?.poQty) === 0 &&
          isButtonEnabled(dataItem.canDelete) &&
          !isInvoiceEditDisabled ? (
            <TextInput
              type={'text'}
              value={value}
              onChange={handleChange}
              valid={!error?.length}
              onFocus={handleHover}
              ocr-data={`itemNumber:${matchItemIndex}`}
            />
          ) : (
            <span>{value}</span>
          )}
        </div>
        {initialInvoice.ocrGenerated && ocrConfidence !== 0 && (
          <OcrIndicator
            isEdited={isEdited}
            ocrConfidence={`${averageOcrConfidence}%`}
            onHover={handleHover}
          />
        )}
      </div>
    </GridCellBase>
  );
};

export default ItemTextField;
